import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavigationTabs } from '../../components/NavigationTabs/NavigationTabs';
import { fetchProducts, fetchUser } from '../../api/productApi';
import { OtherCourses } from '../../components/OtherCourses/OtherCourses';
import { LoadingIndicator } from '../../components/LoadingIndicator/LoadingIndicator';
import { Product, User } from '../../types/product';
import { pluralizeCourses } from '../../utils/stringUtils';
import styles from './Account.module.scss';

export const Account = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState<'products' | 'favorites' | 'account'>('account');
    const [user, setUser] = useState<User | null>(null);
    const [purchasedProducts, setPurchasedProducts] = useState<Product[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);
                const [userData, productsData] = await Promise.all([
                    fetchUser(),
                    fetchProducts()
                ]);

                setUser(userData);
                setPurchasedProducts(productsData.filter(product => product.purchased));
            } catch (err) {
                setError('Не удалось загрузить данные');
                console.error('Ошибка загрузки данных:', err);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, []);

    const handleTabChange = (tab: 'products' | 'favorites' | 'account') => {
        setActiveTab(tab);
        if (tab === 'products') navigate('/');
        else if (tab === 'favorites') navigate('/favorites');
    };

    const handleLastPurchaseClick = () => {
        if (lastPurchase) {
            navigate(`/my-purchase/${lastPurchase.id}`);
        }
    };

    const lastPurchase = purchasedProducts.length > 0
        ? purchasedProducts.sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        )[0]
        : null;

    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <LoadingIndicator message="Загрузка данных..." />
            </div>
        );
    }

    if (error) return <div className={styles.error}>{error}</div>;

    return (
        <div className={styles.account}>
            <main className={styles.content}>
                <section className={`${styles.block} ${styles.userBlock}`}>
                    <h1 className={styles.userName}>
                        {user ? `${user.firstName} ${user.lastName}` : 'Пользователь'}
                    </h1>
                </section>

                {lastPurchase ? (
                    <section
                        className={`${styles.block} ${styles.purchaseBlock}`}
                        onClick={handleLastPurchaseClick}
                    >
                        <h2 className={styles.sectionTitle}>Последняя покупка</h2>
                        <div className={styles.purchaseCard}>
                            <img
                                src={lastPurchase.image || ''}
                                alt={lastPurchase.title}
                                className={styles.purchaseImage}
                            />
                            <div className={styles.purchaseInfo}>
                                <h3 className={styles.purchaseTitle}>{lastPurchase.title}</h3>
                                <span className={styles.videoCount}>
                                    {lastPurchase.videosCount} видео
                                </span>
                            </div>
                        </div>
                    </section>
                ) : (
                    <section className={`${styles.block} ${styles.purchaseBlock}`}>
                        <h2 className={styles.sectionTitle}>У вас пока нет покупок</h2>
                    </section>
                )}

                <div className={styles.actionRow}>
                    <button
                        className={`${styles.block} ${styles.actionBlock} ${!lastPurchase ? styles.disabled : ''}`}
                        onClick={() => navigate('/my-reviews')}
                        disabled={!lastPurchase}
                    >
                        <div className={styles.actionContent}>
                            <span className={styles.starIcon}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill={lastPurchase ? "#FFC107" : "#E0E0E0"}>
                                    <path
                                        d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
                                </svg>
                            </span>
                            <span className={styles.starContent}>Оценить покупки</span>
                        </div>
                        <span className={styles.arrowIcon}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M9 18L15 12L9 6"
                                    stroke={lastPurchase ? "#A5B2B2" : "#E0E0E0"}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </button>

                    <button
                        className={`${styles.block} ${styles.actionBlock} ${purchasedProducts.length === 0 ? styles.disabled : ''}`}
                        onClick={() => navigate('/my-purchases')}
                        disabled={purchasedProducts.length === 0}
                    >
                        <div className={styles.secondActionContent}>
                            <h4>Мои покупки</h4>
                            <p className={styles.courseAmount}>{pluralizeCourses(purchasedProducts.length)}</p>
                        </div>
                        <span className={styles.arrowIcon}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <path
                                    d="M9 18L15 12L9 6"
                                    stroke={purchasedProducts.length === 0 ? "#E0E0E0" : "#A5B2B2"}
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </span>
                    </button>
                </div>

                {purchasedProducts.length > 0 ? (
                    <div className={styles.currentProduct}>
                        <OtherCourses currentProductId={purchasedProducts[0].id}/>
                    </div>
                ) : null}
            </main>
            <footer className={styles.footer}>
                <NavigationTabs
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                />
            </footer>
        </div>
    );
};